import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getStripeEnvironment } from "@/lib/stripe";

export type Subscription = {
  id: string;
  user_id: string;
  provider_subscription_id: string;
  provider_customer_id: string;
  product_id: string;
  price_id: string;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  environment: string;
};

export type Tier = "free" | "starter" | "pro" | "premium_agentic";

export function productToTier(productId?: string | null): Tier {
  switch (productId) {
    case "starter_plan":
      return "starter";
    case "pro_plan":
      return "pro";
    case "premium_agentic_plan":
      return "premium_agentic";
    default:
      return "free";
  }
}

const TIER_RANK: Record<Tier, number> = {
  free: 0,
  starter: 1,
  pro: 2,
  premium_agentic: 3,
};

export function tierAtLeast(current: Tier, required: Tier): boolean {
  return TIER_RANK[current] >= TIER_RANK[required];
}

// Stripe "test" maps to the same DB `environment='sandbox'` bucket used by the
// old Paddle setup so historical rows and quota lookups keep working.
function envToDbValue(env: "test" | "live"): "sandbox" | "live" {
  return env === "live" ? "live" : "sandbox";
}

export function useSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  const dbEnv = envToDbValue(getStripeEnvironment());

  const refetch = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("subscriptions")
      .select("*")
      .eq("user_id", user.id)
      .eq("environment", dbEnv)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setSubscription((data as Subscription | null) ?? null);
    setLoading(false);
  }, [user, dbEnv]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`subscriptions:${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${user.id}` },
        () => refetch(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, refetch]);

  const isActive = !!subscription &&
    ["active", "trialing"].includes(subscription.status) &&
    (!subscription.current_period_end || new Date(subscription.current_period_end) > new Date());

  const tier: Tier = isActive ? productToTier(subscription?.product_id) : "free";

  return {
    subscription,
    tier,
    isActive,
    loading,
    refetch,
    canceledAtPeriodEnd: !!subscription?.cancel_at_period_end,
  };
}
